import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../hooks/useToast'

const CATEGORIAS_DESPESA = ['Fornecedor', 'Embalagem', 'Transporte', 'Aluguel', 'Marketing', 'Outros']

function numero(valor) {
  return parseFloat(String(valor || 0).replace(',', '.')) || 0
}

function moeda(valor) {
  const n = numero(valor)
  return `${n < 0 ? '-' : ''}R$${Math.abs(n).toFixed(2)}`
}

function dataBR(valor) {
  if (!valor) return '-'
  const base = String(valor).slice(0, 10)
  const [ano, mes, dia] = base.split('-')
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : '-'
}

function mesAtual() {
  const hoje = new Date()
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
}

function filtrarMes(lista, campo, periodo) {
  if (!periodo) return lista
  return lista.filter(item => String(item[campo] || '').startsWith(periodo))
}

function addCategoria(acc, categoria, valor) {
  const chave = categoria || 'Sem categoria'
  acc[chave] = (acc[chave] || 0) + valor
  return acc
}

function linhasCategoria(obj) {
  return Object.entries(obj).sort((a, b) => b[1] - a[1])
}

function addConta(acc, codigo, nome, debito = 0, credito = 0) {
  if (!acc[codigo]) acc[codigo] = { codigo, nome, debito: 0, credito: 0 }
  acc[codigo].debito += debito
  acc[codigo].credito += credito
  return acc
}

function montarArvoreBalancete(contas) {
  const grupos = [
    { codigo: '1', nome: 'Ativo', filhos: ['1.1.01', '1.1.02'] },
    { codigo: '2', nome: 'Patrimonio / capital', filhos: ['2.1.01'] },
    { codigo: '3', nome: 'Receitas', filhos: ['3.1.01'] },
    { codigo: '4', nome: 'Custos e despesas', prefixos: ['4.'] },
  ]

  return grupos
    .map(grupo => {
      const filhos = contas.filter(conta =>
        (grupo.filhos || []).includes(conta.codigo) ||
        (grupo.prefixos || []).some(prefixo => conta.codigo.startsWith(prefixo))
      )
      return {
        ...grupo,
        filhos,
        debito: filhos.reduce((s, c) => s + c.debito, 0),
        credito: filhos.reduce((s, c) => s + c.credito, 0),
      }
    })
    .filter(grupo => grupo.filhos.length > 0)
}

export default function Financeiro() {
  const [lancamentos, setLancamentos] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [movimentos, setMovimentos] = useState([])
  const [itens, setItens] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('caixa')
  const [periodo, setPeriodo] = useState(mesAtual())

  const [showForm, setShowForm] = useState(false)
  const [tipo, setTipo] = useState('saida')
  const [descricao, setDescricao] = useState('')
  const [valor, setValor] = useState('')
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const [categoria, setCategoria] = useState('Outros')
  const [saving, setSaving] = useState(false)
  const [confirm, setConfirm] = useState(null)

  const { msg, visible, show } = useToast()

  useEffect(() => { fetchTudo() }, [])

  async function fetchTudo() {
    setLoading(true)
    const [{ data: lanc, error: lancError }, { data: ped, error: pedError }, { data: mov, error: movError }, { data: it, error: itError }] = await Promise.all([
      supabase.from('lancamentos').select('*').order('data', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('pedidos')
        .select('*, pedido_itens(custo_total, quantidade, pedido_item_composicao(quantidade, custo_unitario, itens(categoria)))')
        .order('created_at', { ascending: false }),
      supabase.from('movimentos').select('tipo, quantidade, custo_unitario, created_at, itens(categoria)').order('created_at', { ascending: false }),
      supabase.from('itens').select('estoque, custo, categoria, movimenta_estoque'),
    ])

    const erro = lancError || pedError || movError || itError
    if (erro) {
      console.error('Erro ao carregar financeiro:', erro)
      show(`Erro ao carregar: ${erro.message}`)
    }

    setLancamentos(lanc || [])
    setPedidos(ped || [])
    setMovimentos(mov || [])
    setItens(it || [])
    setLoading(false)
  }

  async function salvar() {
    const valorNumerico = numero(valor)
    if (!descricao.trim()) { show('Informe uma descricao'); return }
    if (valorNumerico <= 0) { show('Valor invalido'); return }
    if (!data) { show('Informe a data'); return }

    setSaving(true)
    const { error } = await supabase.from('lancamentos').insert([{
      tipo,
      descricao: descricao.trim(),
      valor: valorNumerico,
      data,
      categoria: tipo === 'saida' ? categoria : 'Entrada',
    }])

    if (error) {
      console.error('Erro ao salvar lancamento:', error)
      show(`Erro ao salvar: ${error.message}`)
      setSaving(false)
      return
    }

    show('Lancamento salvo!')
    setSaving(false)
    setShowForm(false)
    setDescricao('')
    setValor('')
    setCategoria('Outros')
    setData(new Date().toISOString().slice(0, 10))
    fetchTudo()
  }

  async function excluir(id) {
    const { error } = await supabase.from('lancamentos').delete().eq('id', id)
    if (error) {
      show(`Erro ao remover: ${error.message}`)
      setConfirm(null)
      return
    }
    show('Removido')
    setConfirm(null)
    fetchTudo()
  }

  const dados = useMemo(() => {
    const pedidosRecebidos = pedidos.filter(p => ['confirmado', 'entregue'].includes(p.status))
    const pedidosPendentes = pedidos.filter(p => !['confirmado', 'entregue'].includes(p.status))
    const pedidosPeriodo = filtrarMes(pedidosRecebidos, 'created_at', periodo)
    const lancPeriodo = filtrarMes(lancamentos, 'data', periodo)
    const movPeriodo = filtrarMes(movimentos, 'created_at', periodo)

    const vendasGeral = pedidosRecebidos.reduce((s, p) => s + numero(p.total), 0)
    const vendasPendentesGeral = pedidosPendentes.reduce((s, p) => s + numero(p.total), 0)
    const comprasGeral = movimentos
      .filter(m => m.tipo === 'entrada')
      .reduce((s, m) => s + numero(m.quantidade) * numero(m.custo_unitario), 0)
    const ajustesGeral = lancamentos.reduce((s, l) => (
      l.tipo === 'entrada' ? s + numero(l.valor) : s - numero(l.valor)
    ), 0)

    const receita = pedidosPeriodo.reduce((s, p) => s + numero(p.total), 0)
    const cmv = pedidosPeriodo.reduce((s, p) => (
      s + (p.pedido_itens || []).reduce((ss, pi) => ss + numero(pi.custo_total) * numero(pi.quantidade), 0)
    ), 0)
    const cmvPorCategoria = pedidosPeriodo.reduce((acc, pedido) => {
      for (const pi of (pedido.pedido_itens || [])) {
        const qtdPedidoItem = numero(pi.quantidade) || 1
        const custoItemTotal = numero(pi.custo_total) * qtdPedidoItem
        let custoComposicao = 0

        for (const comp of (pi.pedido_item_composicao || [])) {
          const valorComp = numero(comp.quantidade) * numero(comp.custo_unitario) * qtdPedidoItem
          custoComposicao += valorComp
          addCategoria(acc, comp.itens?.categoria, valorComp)
        }

        const naoClassificado = custoItemTotal - custoComposicao
        if (naoClassificado > 0.01) addCategoria(acc, 'Montagem / mao de obra', naoClassificado)
      }
      return acc
    }, {})
    const despesas = lancPeriodo.filter(l => l.tipo === 'saida').reduce((s, l) => s + numero(l.valor), 0)
    const comprasPeriodo = movPeriodo
      .filter(m => m.tipo === 'entrada')
      .reduce((s, m) => s + numero(m.quantidade) * numero(m.custo_unitario), 0)
    const comprasPorCategoria = movPeriodo
      .filter(m => m.tipo === 'entrada')
      .reduce((acc, m) => addCategoria(acc, m.itens?.categoria, numero(m.quantidade) * numero(m.custo_unitario)), {})
    const perdasPeriodo = movPeriodo
      .filter(m => m.tipo === 'inutilizado')
      .reduce((s, m) => s + numero(m.quantidade) * numero(m.custo_unitario), 0)
    const perdasPorCategoria = movPeriodo
      .filter(m => m.tipo === 'inutilizado')
      .reduce((acc, m) => addCategoria(acc, m.itens?.categoria, numero(m.quantidade) * numero(m.custo_unitario)), {})
    const entradasManuaisPeriodo = lancPeriodo.filter(l => l.tipo === 'entrada').reduce((s, l) => s + numero(l.valor), 0)

    const despesasPorCategoria = lancPeriodo
      .filter(l => l.tipo === 'saida')
      .reduce((acc, l) => {
        const chave = l.categoria || 'Outros'
        acc[chave] = (acc[chave] || 0) + numero(l.valor)
        return acc
      }, {})

    const valorEstoque = itens.reduce((s, item) => {
      if (item.movimenta_estoque === false) return s
      const estoque = numero(item.estoque)
      return s + (estoque > 0 ? estoque * numero(item.custo) : 0)
    }, 0)
    const estoquePorCategoria = itens.reduce((acc, item) => {
      if (item.movimenta_estoque === false) return acc
      const estoque = numero(item.estoque)
      if (estoque > 0) addCategoria(acc, item.categoria, estoque * numero(item.custo))
      return acc
    }, {})

    const lucroBruto = receita - cmv
    const resultadoLiquido = lucroBruto - despesas - perdasPeriodo
    const saldoCaixa = ajustesGeral + vendasGeral - comprasGeral
    const disponibilidades = Math.max(0, saldoCaixa)
    const caixaARegularizar = Math.max(0, -saldoCaixa)
    const razao = []

    for (const p of pedidosPeriodo) {
      const valor = numero(p.total)
      const custo = (p.pedido_itens || []).reduce((s, pi) => s + numero(pi.custo_total) * numero(pi.quantidade), 0)
      if (valor > 0) {
        razao.push({
          data: String(p.created_at).slice(0, 10),
          historico: `Venda recebida${p.cliente_nome ? ` - ${p.cliente_nome}` : ''}`,
          debito: '1.1.01 Caixa / disponibilidades',
          credito: '3.1.01 Receita de vendas',
          valor,
        })
      }
      if (custo > 0) {
        razao.push({
          data: String(p.created_at).slice(0, 10),
          historico: `Baixa do custo vendido${p.cliente_nome ? ` - ${p.cliente_nome}` : ''}`,
          debito: '4.1.01 CMV',
          credito: '1.1.02 Estoque',
          valor: custo,
        })
      }
    }

    for (const m of movPeriodo) {
      const valor = numero(m.quantidade) * numero(m.custo_unitario)
      if (valor <= 0) continue
      if (m.tipo === 'entrada') {
        razao.push({
          data: String(m.created_at).slice(0, 10),
          historico: `Compra de estoque${m.itens?.categoria ? ` - ${m.itens.categoria}` : ''}`,
          debito: '1.1.02 Estoque',
          credito: '1.1.01 Caixa / disponibilidades',
          valor,
        })
      }
      if (m.tipo === 'inutilizado') {
        razao.push({
          data: String(m.created_at).slice(0, 10),
          historico: `Produto inutilizado${m.itens?.categoria ? ` - ${m.itens.categoria}` : ''}`,
          debito: '4.3.01 Perdas com produtos inutilizados',
          credito: '1.1.02 Estoque',
          valor,
        })
      }
    }

    for (const l of lancPeriodo) {
      const valor = numero(l.valor)
      if (valor <= 0) continue
      if (l.tipo === 'entrada') {
        razao.push({
          data: l.data,
          historico: l.descricao,
          debito: '1.1.01 Caixa / disponibilidades',
          credito: '2.1.01 Entradas manuais / capital',
          valor,
        })
      } else {
        razao.push({
          data: l.data,
          historico: l.descricao,
          debito: `4.2.01 Despesas - ${l.categoria || 'Outros'}`,
          credito: '1.1.01 Caixa / disponibilidades',
          valor,
        })
      }
    }

    razao.sort((a, b) => String(b.data).localeCompare(String(a.data)))
    const balanceteMap = razao.reduce((acc, l) => {
      const [codDeb, ...nomeDeb] = l.debito.split(' ')
      const [codCred, ...nomeCred] = l.credito.split(' ')
      addConta(acc, codDeb, nomeDeb.join(' '), l.valor, 0)
      addConta(acc, codCred, nomeCred.join(' '), 0, l.valor)
      return acc
    }, {})
    const balancete = Object.values(balanceteMap).sort((a, b) => a.codigo.localeCompare(b.codigo))
    const balanceteArvore = montarArvoreBalancete(balancete)
    const totalDebito = balancete.reduce((s, c) => s + c.debito, 0)
    const totalCredito = balancete.reduce((s, c) => s + c.credito, 0)

    return {
      vendasGeral,
      comprasGeral,
      ajustesGeral,
      saldoCaixa,
      disponibilidades,
      caixaARegularizar,
      pedidosPeriodo,
      pedidosPendentes,
      receita,
      vendasPendentesGeral,
      cmv,
      cmvPorCategoria,
      lucroBruto,
      margemBruta: receita > 0 ? (lucroBruto / receita) * 100 : 0,
      despesas,
      despesasPorCategoria,
      resultadoLiquido,
      margemLiquida: receita > 0 ? (resultadoLiquido / receita) * 100 : 0,
      comprasPeriodo,
      comprasPorCategoria,
      perdasPeriodo,
      perdasPorCategoria,
      entradasManuaisPeriodo,
      fluxoPeriodo: receita + entradasManuaisPeriodo - comprasPeriodo - despesas,
      valorEstoque,
      estoquePorCategoria,
      totalAtivo: disponibilidades + valorEstoque,
      razao,
      balancete,
      balanceteArvore,
      totalDebito,
      totalCredito,
    }
  }, [itens, lancamentos, movimentos, pedidos, periodo])

  const corValor = valor => numero(valor) >= 0 ? 'var(--green)' : '#e53935'

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Financeiro</h1>
        {view === 'caixa' && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Lancamento</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {['caixa', 'dre', 'balanco', 'razao', 'balancete'].map(v => (
          <button key={v} className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView(v)}>
            {v === 'caixa' ? 'Caixa' : v === 'dre' ? 'DRE' : v === 'balanco' ? 'Balanco' : v === 'razao' ? 'Razao' : 'Balancete'}
          </button>
        ))}
      </div>

      {(view === 'dre' || view === 'balanco' || view === 'razao' || view === 'balancete') && (
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Periodo (mes)</label>
          <input type="month" value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ maxWidth: 180 }} />
        </div>
      )}

      {loading && <div className="spinner">Carregando...</div>}

      {!loading && view === 'caixa' && (
        <div>
          <div className="cost-box" style={{ marginBottom: 16, background: dados.saldoCaixa >= 0 ? '#f1f8e9' : '#fce4ec' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Disponibilidades</div>
            <div className="cost-row total">
              <span>Saldo atual</span>
              <span style={{ color: corValor(dados.saldoCaixa) }}>{moeda(dados.saldoCaixa)}</span>
            </div>
            <div className="cost-row" style={{ fontSize: 12 }}>
              <span>Recebido em vendas confirmadas/entregues</span>
              <span style={{ color: 'var(--green)' }}>+{moeda(dados.vendasGeral)}</span>
            </div>
            <div className="cost-row" style={{ fontSize: 12 }}>
              <span>Entradas manuais / ajustes</span>
              <span style={{ color: corValor(dados.ajustesGeral) }}>{dados.ajustesGeral > 0 ? '+' : ''}{moeda(dados.ajustesGeral)}</span>
            </div>
            <div className="cost-row" style={{ fontSize: 12 }}>
              <span>Compras de estoque pagas</span>
              <span style={{ color: '#e53935' }}>-{moeda(dados.comprasGeral)}</span>
            </div>
            {dados.vendasPendentesGeral > 0 && (
              <div className="cost-row" style={{ fontSize: 12 }}>
                <span>Orcamentos pendentes</span>
                <span style={{ color: 'var(--text-secondary)' }}>{moeda(dados.vendasPendentesGeral)}</span>
              </div>
            )}
          </div>

          <div className="section-label">Ajustes e despesas manuais</div>
          {lancamentos.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, padding: '20px 0' }}>
              Nenhum lancamento manual.<br />Use para saldo inicial, despesas fixas e ajustes.
            </div>
          )}

          {lancamentos.map(l => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.descricao}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{dataBR(l.data)} - {l.categoria}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: l.tipo === 'entrada' ? 'var(--green)' : '#c2185b' }}>
                  {l.tipo === 'entrada' ? '+' : '-'}{moeda(l.valor)}
                </span>
                <button className="btn btn-danger btn-sm" onClick={() => setConfirm(l)}>x</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && view === 'dre' && (
        <div>
          <div className="cost-box" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 12, letterSpacing: 0.8 }}>
              Demonstracao do Resultado - {periodo}
            </div>
            <div className="cost-row">
              <span>Receita bruta</span>
              <span style={{ color: 'var(--green)', fontWeight: 600 }}>{moeda(dados.receita)}</span>
            </div>
            <div className="cost-row">
              <span style={{ paddingLeft: 12 }}>(-) CMV</span>
              <span style={{ color: '#e53935' }}>-{moeda(dados.cmv)}</span>
            </div>
            <div className="cost-row" style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
              <span style={{ fontWeight: 600 }}>Lucro bruto</span>
              <span style={{ fontWeight: 700, color: corValor(dados.lucroBruto) }}>{moeda(dados.lucroBruto)}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right', marginBottom: 10 }}>
              margem bruta {dados.margemBruta.toFixed(1)}%
            </div>

            {Object.entries(dados.despesasPorCategoria).map(([cat, val]) => (
              <div key={cat} className="cost-row">
                <span style={{ paddingLeft: 12 }}>(-) {cat}</span>
                <span style={{ color: '#e53935' }}>-{moeda(val)}</span>
              </div>
            ))}

            {dados.perdasPeriodo > 0 && (
              <div className="cost-row">
                <span style={{ paddingLeft: 12 }}>(-) Produtos inutilizados</span>
                <span style={{ color: '#e53935' }}>-{moeda(dados.perdasPeriodo)}</span>
              </div>
            )}

            <div className="cost-row total" style={{ marginTop: 6 }}>
              <span>Resultado liquido</span>
              <span style={{ color: corValor(dados.resultadoLiquido) }}>{moeda(dados.resultadoLiquido)}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right' }}>
              margem liquida {dados.margemLiquida.toFixed(1)}%
            </div>
          </div>

          <div className="cost-box" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: 0.8 }}>
              CMV por categoria - {periodo}
            </div>
            {linhasCategoria(dados.cmvPorCategoria).length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: 8 }}>Sem custo vendido no periodo.</div>
            )}
            {linhasCategoria(dados.cmvPorCategoria).map(([cat, val]) => (
              <div key={cat} className="cost-row" style={{ fontSize: 13 }}>
                <span>{cat}</span>
                <span style={{ color: '#e53935' }}>{moeda(val)}</span>
              </div>
            ))}
          </div>

          <div className="cost-box" style={{ background: '#f5f5f5' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: 0.8 }}>
              Fluxo de caixa - {periodo}
            </div>
            <div className="cost-row" style={{ fontSize: 13 }}>
              <span>Entradas de vendas</span>
              <span style={{ color: 'var(--green)' }}>+{moeda(dados.receita)}</span>
            </div>
            {dados.entradasManuaisPeriodo > 0 && (
              <div className="cost-row" style={{ fontSize: 13 }}>
                <span>Entradas manuais</span>
                <span style={{ color: 'var(--green)' }}>+{moeda(dados.entradasManuaisPeriodo)}</span>
              </div>
            )}
            <div className="cost-row" style={{ fontSize: 13 }}>
              <span>Compras de estoque</span>
              <span style={{ color: '#e53935' }}>-{moeda(dados.comprasPeriodo)}</span>
            </div>
            <div className="cost-row" style={{ fontSize: 13 }}>
              <span>Despesas</span>
              <span style={{ color: '#e53935' }}>-{moeda(dados.despesas)}</span>
            </div>
            <div className="cost-row total" style={{ fontSize: 13 }}>
              <span>Saldo do periodo</span>
              <span style={{ color: corValor(dados.fluxoPeriodo) }}>{moeda(dados.fluxoPeriodo)}</span>
            </div>
          </div>

          {(linhasCategoria(dados.comprasPorCategoria).length > 0 || linhasCategoria(dados.perdasPorCategoria).length > 0) && (
            <div className="cost-box" style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: 0.8 }}>
                Estoque movimentado por categoria
              </div>
              {linhasCategoria(dados.comprasPorCategoria).map(([cat, val]) => (
                <div key={`compra-${cat}`} className="cost-row" style={{ fontSize: 13 }}>
                  <span>Compra - {cat}</span>
                  <span style={{ color: '#e53935' }}>{moeda(val)}</span>
                </div>
              ))}
              {linhasCategoria(dados.perdasPorCategoria).map(([cat, val]) => (
                <div key={`perda-${cat}`} className="cost-row" style={{ fontSize: 13 }}>
                  <span>Inutilizado - {cat}</span>
                  <span style={{ color: '#e53935' }}>{moeda(val)}</span>
                </div>
              ))}
            </div>
          )}

          {dados.pedidosPeriodo.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, padding: 16 }}>
              Nenhum pedido confirmado neste periodo.
            </div>
          )}
        </div>
      )}

      {!loading && view === 'balanco' && (
        <div>
          <div className="cost-box" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.8 }}>
              Ativo
            </div>
            <div style={{ fontSize: 11, color: '#bbb', fontWeight: 600, marginBottom: 4, paddingLeft: 4 }}>Circulante</div>
            <div className="cost-row">
              <span style={{ paddingLeft: 12 }}>Caixa / disponibilidades</span>
              <span style={{ color: corValor(dados.saldoCaixa), fontWeight: 600 }}>{moeda(dados.saldoCaixa)}</span>
            </div>
            <div className="cost-row">
              <span style={{ paddingLeft: 12 }}>Estoque a custo</span>
              <span style={{ fontWeight: 600 }}>{moeda(dados.valorEstoque)}</span>
            </div>
            {linhasCategoria(dados.estoquePorCategoria).map(([cat, val]) => (
              <div key={cat} className="cost-row" style={{ fontSize: 13 }}>
                <span style={{ paddingLeft: 24 }}>{cat}</span>
                <span>{moeda(val)}</span>
              </div>
            ))}
            <div className="cost-row total">
              <span>Total Ativo</span>
              <span>{moeda(dados.totalAtivo)}</span>
            </div>
          </div>

          <div className="cost-box">
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.8 }}>
              Base contabil simplificada
            </div>
            <div className="cost-row">
              <span style={{ paddingLeft: 12 }}>Receita do periodo</span>
              <span style={{ color: 'var(--green)' }}>{moeda(dados.receita)}</span>
            </div>
            <div className="cost-row">
              <span style={{ paddingLeft: 12 }}>Resultado do periodo</span>
              <span style={{ color: corValor(dados.resultadoLiquido), fontWeight: 600 }}>{moeda(dados.resultadoLiquido)}</span>
            </div>
            <div className="cost-row">
              <span style={{ paddingLeft: 12 }}>Valor em estoque</span>
              <span>{moeda(dados.valorEstoque)}</span>
            </div>
            <div className="cost-row">
              <span style={{ paddingLeft: 12 }}>Vendas recebidas acumuladas</span>
              <span style={{ color: 'var(--green)' }}>{moeda(dados.vendasGeral)}</span>
            </div>
          </div>

          <div style={{ fontSize: 11, color: '#ccc', textAlign: 'center', marginTop: 12 }}>
            Balanco simplificado com conta unica de disponibilidades.
          </div>
        </div>
      )}

      {!loading && view === 'razao' && (
        <div>
          <div className="cost-box">
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.8 }}>
              Razao contabil - {periodo}
            </div>
            {dados.razao.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: 12 }}>
                Nenhum lancamento contabil neste periodo.
              </div>
            )}
            {dados.razao.map((l, idx) => (
              <div key={`${l.data}-${idx}`} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{dataBR(l.data)}</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{moeda(l.valor)}</div>
                </div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>{l.historico}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  <div>Debito: {l.debito}</div>
                  <div>Credito: {l.credito}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && view === 'balancete' && (
        <div>
          <div className="cost-box">
            <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.8 }}>
              Balancete de verificacao - {periodo}
            </div>
            {dados.balancete.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: 12 }}>
                Nenhum movimento neste periodo.
              </div>
            )}
            {dados.balancete.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 420 }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1.6fr 0.9fr 0.9fr 0.9fr',
                    gap: 8,
                    padding: '6px 0',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 10,
                    color: '#aaa',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}>
                    <span>Conta</span>
                    <span style={{ textAlign: 'right' }}>Debito</span>
                    <span style={{ textAlign: 'right' }}>Credito</span>
                    <span style={{ textAlign: 'right' }}>Saldo</span>
                  </div>

                  {dados.balanceteArvore.map(grupo => {
                    const saldoGrupo = grupo.debito - grupo.credito
                    return (
                      <div key={grupo.codigo}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1.6fr 0.9fr 0.9fr 0.9fr',
                          gap: 8,
                          alignItems: 'center',
                          padding: '10px 0',
                          borderBottom: '1px solid var(--border)',
                          fontSize: 12,
                          background: 'rgba(0,0,0,0.015)',
                        }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 900, fontSize: 12 }}>{grupo.codigo}</div>
                            <div style={{ fontWeight: 800 }}>{grupo.nome}</div>
                          </div>
                          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{moeda(grupo.debito)}</span>
                          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{moeda(grupo.credito)}</span>
                          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: corValor(saldoGrupo), fontWeight: 900 }}>{moeda(saldoGrupo)}</span>
                        </div>

                        {grupo.filhos.map(conta => {
                          const saldo = conta.debito - conta.credito
                          return (
                            <div key={conta.codigo} style={{
                              display: 'grid',
                              gridTemplateColumns: '1.6fr 0.9fr 0.9fr 0.9fr',
                              gap: 8,
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: '1px solid var(--border)',
                              fontSize: 12,
                            }}>
                              <div style={{ minWidth: 0, paddingLeft: 14 }}>
                                <div style={{ fontWeight: 800, fontSize: 12 }}>{conta.codigo}</div>
                                <div style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conta.nome}</div>
                              </div>
                              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moeda(conta.debito)}</span>
                              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moeda(conta.credito)}</span>
                              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: corValor(saldo), fontWeight: 700 }}>{moeda(saldo)}</span>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1.6fr 0.9fr 0.9fr 0.9fr',
                    gap: 8,
                    padding: '10px 0 2px',
                    fontSize: 12,
                    fontWeight: 800,
                  }}>
                    <span>Total</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moeda(dados.totalDebito)}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moeda(dados.totalCredito)}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: Math.abs(dados.totalDebito - dados.totalCredito) < 0.01 ? 'var(--green)' : '#e53935' }}>
                      {moeda(dados.totalDebito - dados.totalCredito)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">Novo lancamento</div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className={`btn btn-sm ${tipo === 'entrada' ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1 }} onClick={() => setTipo('entrada')}>+ Entrada</button>
              <button className={`btn btn-sm ${tipo === 'saida' ? 'btn-danger' : 'btn-ghost'}`} style={{ flex: 1 }} onClick={() => setTipo('saida')}>- Despesa</button>
            </div>

            <div style={{ background: '#f9f0f5', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#888', marginBottom: 14 }}>
              {tipo === 'entrada'
                ? 'Use para saldo inicial em caixa, recebimentos avulsos ou ajustes positivos.'
                : 'Use para despesas fixas e gastos operacionais. Compras de estoque ja saem pelo modulo Entradas.'}
            </div>

            <div className="field">
              <label>Descricao</label>
              <input
                placeholder={tipo === 'entrada' ? 'Ex: Saldo inicial' : 'Ex: Aluguel de maio'}
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                autoFocus
              />
            </div>

            <div className="row">
              <div className="field">
                <label>Valor (R$)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={valor}
                  onChange={e => setValor(e.target.value)}
                  onBlur={() => { const v = numero(valor); if (v > 0) setValor(v.toFixed(2)) }}
                />
              </div>
              <div className="field">
                <label>Data</label>
                <input type="date" value={data} onChange={e => setData(e.target.value)} />
              </div>
            </div>

            {tipo === 'saida' && (
              <div className="field">
                <label>Categoria</label>
                <select value={categoria} onChange={e => setCategoria(e.target.value)}>
                  {CATEGORIAS_DESPESA.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Cancelar</button>
              <button className={`btn ${tipo === 'entrada' ? 'btn-primary' : 'btn-danger'}`} style={{ flex: 2 }} onClick={salvar} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <p style={{ fontSize: 15, fontWeight: 600, textAlign: 'center', marginBottom: 8 }}>Excluir lancamento?</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 20 }}>
              {confirm.descricao} - {moeda(confirm.valor)}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancelar</button>
              <button className="btn btn-danger" style={{ flex: 1 }} onClick={() => excluir(confirm.id)}>Excluir</button>
            </div>
          </div>
        </div>
      )}

      <div className={`toast ${visible ? 'show' : ''}`}>{msg}</div>
    </div>
  )
}
